import React, { useState, useRef } from 'react';
import Slider from 'react-slick';
import Slide from './slide';
import { useSiteData, DataContextProps } from '@context';

interface CarouselSlide {
  pageId: string;
  backgroundColor: string;
  slideIndex: number;
}

interface CustomArrowProps {
  direction: 'prev' | 'next';
  sliderRef: React.RefObject<Slider>;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}

/**
 * Custom arrow for carousel. Added classes to help with styling.
 */
const CustomArrow: React.FC<CustomArrowProps> = ({
  direction,
  sliderRef,
  onClick,
}) => {
  const isPrev = direction === 'prev';
  const handleClick = () => {
    onClick?.();
    if (isPrev) {
      sliderRef.current?.slickPrev();
    } else {
      sliderRef.current?.slickNext();
    }
  };

  return (
    <div className={`arrow-wrapper arrow-${direction}`}>
      <div className="arrow-container">
        <button
          data-button-type="control"
          data-button-variation="outline"
          aria-label={isPrev ? 'Previous' : 'Next'}
          onClick={handleClick}
        >
          <i
            className={
              isPrev ? 'icon-arrow-left-medium' : 'icon-arrow-right-medium'
            }
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
  );
};

/**
 * Carousel.
 */
const Carousel: React.FC<{ slides: CarouselSlide[] }> = ({ slides }) => {
  const { siteData }: DataContextProps = useSiteData();
  const [sliderBackgroundColor, setSliderBackgroundColor] =
    useState<string>('orange');

  const sliderRef = useRef<Slider | null>(null);

  if (siteData) {
    const { allEmpowerMePages } = siteData;
    const sortedSlides = slides.sort((a, b) => {
      if (a.slideIndex < b.slideIndex) {
        return -1;
      }
      if (a.slideIndex > b.slideIndex) {
        return 1;
      }
      return 0;
    });

    const settings = {
      dots: true,
      infinite: true,
      fade: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      swipeToSlide: true,
      prevArrow: <CustomArrow direction="prev" sliderRef={sliderRef} />,
      nextArrow: <CustomArrow direction="next" sliderRef={sliderRef} />,

      beforeChange: (_current: number, nextSlide: number) => {
        setSliderBackgroundColor(sortedSlides[nextSlide].backgroundColor);
      },
    };

    /**
     * Render.
     */
    if (allEmpowerMePages?.nodes && allEmpowerMePages?.nodes.length > 0) {
      return (
        <div className="carousel-container">
          <Slider
            ref={sliderRef}
            {...settings}
            className={`background-${sliderBackgroundColor}`}
          >
            {sortedSlides.map((slide) => {
              if (allEmpowerMePages.nodes) {
                for (const pageData of allEmpowerMePages.nodes) {
                  const { frontmatter } = pageData;
                  if (
                    slide.pageId === frontmatter.id &&
                    siteData.currentLocale === frontmatter.language
                  ) {
                    return (
                      <Slide
                        slideData={frontmatter}
                        backgroundColor={slide.backgroundColor}
                        key={slide.pageId}
                      />
                    );
                  }
                }
              }
              return null;
            })}
          </Slider>
        </div>
      );
    }
  }
};
export default Carousel;
